import { createApp, h, watch, type App, type Component } from "vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import type { LocaleMessage } from "@intlify/core-base";
import en from "@dashboard/l10n/locales/en.json";
import { safeMessageCompiler } from "@dashboard/l10n/safeMessageCompiler";
import { resolveBrandConfig } from "@dashboard/brands/brandConfig";
import BrandDashboard from "@dashboard/brands/BrandDashboard.vue";
import CoreServicesBrand from "@dashboard/brands/CoreServicesBrand.vue";
import BrandHeader from "@dashboard/brands/BrandHeader.vue";
import BrandFooter from "@dashboard/brands/BrandFooter.vue";
import { useLocaleStore } from "@store/useLocaleStore";

interface MountTarget {
  component: Component;
  props?: Record<string, unknown>;
}

interface BrandEntryOptions {
  brand: string;
  dashboard?: MountTarget;
  header?: MountTarget;
  footer?: MountTarget;
  locale?: string;
  fallbackLocale?: string;
  messages?: Record<string, LocaleMessage>;
}

const defaultMessages = { en_US: en };

/** Mounts the three independent production roots used by dashboard and error pages. */
export function mountBrandEntry(options: BrandEntryOptions): App<Element>[] {
  const pinia = createPinia();
  const messages: Record<string, LocaleMessage> = options.messages ?? defaultMessages;
  const localeStore = useLocaleStore(pinia);
  localeStore.initialize(options.locale ?? "en_US", Object.keys(messages));
  const apps: App<Element>[] = [];

  for (const [selector, target] of [
    ["#core-services", options.dashboard],
    ["#header", options.header],
    ["#footer", options.footer],
  ] as const) {
    const element = document.querySelector(selector);
    // Error pages intentionally omit the dashboard root.
    if (!element || !target) continue;

    const i18n = createI18n({
      legacy: false,
      locale: localeStore.locale,
      fallbackLocale: options.fallbackLocale ?? "en_US",
      messages,
      messageCompiler: safeMessageCompiler,
      // Unknown service and region names are API data, not translation keys.
      fallbackFormat: false,
      missingWarn: false,
      fallbackWarn: false,
    });

    const stopLocaleSync = watch(
      () => localeStore.locale,
      (locale) => {
        i18n.global.locale.value = locale;
      },
      { immediate: true },
    );
    // Every dashboard uses the same shell; header/footer keep their own roots.
    const app = (selector === "#core-services"
      ? createApp({
          render: () => h(BrandDashboard, null, {
            default: () => h(target.component, target.props),
          }),
        })
      : createApp(target.component, target.props)).use(pinia).use(i18n);
    app.onUnmount(stopLocaleSync);
    app.mount(element);
    apps.push(app);
  }

  return apps;
}

/** Standard composition used by the eleven brands without custom chrome. */
export function mountSharedBrandEntry(brand: string): void {
  const config = resolveBrandConfig(brand);

  mountBrandEntry({
    brand,
    dashboard: { component: CoreServicesBrand, props: { brand } },
    header: {
      component: BrandHeader,
      props: {
        logoId: config.logoId,
        logoHref: config.logoHref,
        logoElement: config.logoElement,
        bssKeys: config.bssKeys,
      },
    },
    footer: {
      component: BrandFooter,
      props: {
        copyrightYearFrom: config.copyrightYearFrom,
        copyrightExtraText: config.copyrightExtraText,
        links: config.footerLinks,
        footer: config.footer,
        bssKeys: config.bssKeys,
      },
    },
  });
}
